// Tracks whether the focused-conversation realtime channel for a
// given conversation is currently SUBSCRIBED. Other hooks read this
// to decide how aggressively to poll: when realtime is healthy, the
// 8s polling fallback in useConversationMessages doesn't need to
// fire — the realtime stream is delivering events directly.

import { useSyncExternalStore } from 'react';

type Status = 'unknown' | 'subscribed' | 'closed';

const status = new Map<string, Status>();
const listeners = new Set<() => void>();

const emit = () => {
  for (const fn of listeners) fn();
};

export const setRealtimeStatus = (conversationId: string, next: Status) => {
  if (status.get(conversationId) === next) return;
  status.set(conversationId, next);
  emit();
};

export const clearRealtimeStatus = (conversationId: string) => {
  if (!status.has(conversationId)) return;
  status.delete(conversationId);
  emit();
};

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useRealtimeHealth = (conversationId: string | null): Status =>
  useSyncExternalStore(
    subscribe,
    () => (conversationId ? status.get(conversationId) ?? 'unknown' : 'unknown'),
    () => 'unknown',
  );
